import { MinidocToolbarEditor } from 'minidoc-editor';
import { showToast } from '@components/toaster';
import {
  captureMedia,
  captureVoiceNote,
  isCapturePanelOpen,
  prefersNativeCapture,
} from '@components/media-capture';
import { Internationalize } from 'shared/intl';
import { Mediable } from '@components/media-card';
import { DownloadableFile } from 'server/types';
import { setCurrentRange } from 'client/lib/minidoc';
import { setRecording } from '@components/media-card/media-uploader';

export function withEditorRange(editor: MinidocToolbarEditor, range: Range, fn: () => void) {
  if (!editor.root.isConnected) {
    return;
  }
  setCurrentRange(range, editor);
  fn();
}

export function insertIntoEditor(
  editor: MinidocToolbarEditor,
  range: Range,
  files: Array<File | DownloadableFile>,
) {
  if (!files.length) {
    return;
  }
  withEditorRange(editor, range, () => (editor as unknown as Mediable).insertMedia(files));
}

export type EditorCaptureMode = 'voice' | 'video' | 'photo';

function editorSignal(editor: MinidocToolbarEditor) {
  const controller = new AbortController();
  setRecording(editor, true);
  let misses = 0;
  const timer = setInterval(() => {
    misses = editor.root.isConnected ? 0 : misses + 1;
    if (misses >= 2) {
      clearInterval(timer);
      controller.abort();
    }
  }, 500);
  return {
    signal: controller.signal,
    onClosed: () => {
      clearInterval(timer);
      setRecording(editor, false);
    },
  };
}

export function captureIntoEditor(
  editor: MinidocToolbarEditor,
  range: Range,
  mode: EditorCaptureMode,
  intl: Internationalize,
) {
  const insert = (files: File[]) => insertIntoEditor(editor, range, files);
  if (isCapturePanelOpen()) {
    showToast({
      type: 'warn',
      title: intl('Recording in progress'),
      message: intl('Finish or cancel the open recording first.'),
    });
    return;
  }
  if (mode === 'voice') {
    void captureVoiceNote(editorSignal(editor)).then((file) => {
      if (file) {
        insertIntoEditor(editor, range, [file]);
      }
    });
    return;
  }
  captureMedia(mode, insert, prefersNativeCapture() ? {} : editorSignal(editor));
}
